import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Aperture, Menu, Moon, Sun, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toggleTheme, useTheme } from "../lib/theme";

const LANGS = ["it", "en", "de"];

export const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Nav = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: t("nav.services"), href: "#servizi", tid: "servizi" },
    { label: t("nav.portfolio"), href: "#portfolio", tid: "portfolio" },
    { label: t("nav.manifesto"), href: "#manifesto", tid: "manifesto" },
    { label: t("nav.about"), href: "#chi-siamo", tid: "chi-siamo" },
    { label: t("nav.pricing"), href: "#tariffe", tid: "tariffe" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (document.querySelector(href)) scrollTo(href);
    else window.location.assign(`/${href}`);
  };

  const themeToggle = (
    <button
      onClick={toggleTheme}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 text-[#A0A0A0] transition-[border-color,color,background-color] duration-300 hover:border-[#D6705A] hover:text-[#D6705A] sm:h-8 sm:w-8"
      data-testid="theme-toggle"
      aria-label="Cambia tema chiaro/scuro"
      title="Tema chiaro / scuro"
    >
      {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
    </button>
  );

  const langSwitch = (
    <div className="flex shrink-0 items-center gap-0.5 rounded-full border border-white/10 px-0.5 py-0.5 sm:gap-1 sm:px-1 sm:py-1" data-testid="lang-switcher">
      {LANGS.map((lng) => (
        <button
          key={lng}
          onClick={() => i18n.changeLanguage(lng)}
          className={`rounded-full px-1.5 py-0.5 font-code text-[10px] uppercase tracking-[0.15em] transition-[background-color,color] duration-300 sm:px-2.5 sm:py-1 ${
            i18n.language === lng ? "bg-[#D6705A] text-[#0D0F12]" : "text-[#A0A0A0] hover:text-[#F9F8F6]"
          }`}
          data-testid={`lang-switch-${lng}`}
          aria-label={`Lingua: ${lng.toUpperCase()}`}
          aria-pressed={i18n.language === lng}
        >
          {lng}
        </button>
      ))}
    </div>
  );

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0D0F12]/70 border-b border-white/5" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          onClick={() => {
            if (window.location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
            else window.location.assign("/");
          }}
          className="group flex shrink-0 items-center gap-2.5"
          data-testid="nav-logo"
        >
          <Aperture className="h-5 w-5 text-[#D6705A] transition-transform duration-500 group-hover:rotate-90" strokeWidth={1.5} />
          <span className="whitespace-nowrap font-display text-base tracking-wide text-[#F9F8F6] sm:text-lg">
            L&rsquo;Obiettivo <span className="italic text-[#D6705A]">Immobiliare</span>
          </span>
        </button>

        <nav className="hidden items-center gap-4 pl-8 lg:gap-5 lg:pl-12 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="whitespace-nowrap font-code text-[11px] uppercase tracking-[0.2em] text-[#A0A0A0] transition-colors duration-300 hover:text-[#F9F8F6]"
              data-testid={`nav-link-${l.tid}`}
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-3">
            {themeToggle}
            {langSwitch}
          </div>
          <button
            onClick={() => go("#prenota")}
            className="whitespace-nowrap rounded-full border border-[#D6705A]/60 px-5 py-2.5 font-code text-[11px] uppercase tracking-[0.2em] text-[#D6705A] transition-[background-color,color,transform] duration-300 hover:bg-[#D6705A] hover:text-[#0D0F12] hover:-translate-y-0.5"
            data-testid="nav-cta-booking"
          >
            <span className="2xl:hidden">{t("nav.ctaShort")}</span>
            <span className="hidden 2xl:inline">{t("nav.cta")}</span>
          </button>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          {themeToggle}
          {langSwitch}
          <button className="flex h-9 w-9 shrink-0 items-center justify-center text-[#F9F8F6]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label={t("nav.menu")}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden backdrop-blur-xl bg-[#0D0F12]/90 border-t border-white/5 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} className="block font-code text-xs uppercase tracking-[0.25em] text-[#A0A0A0]" data-testid={`nav-mobile-${l.tid}`}>
              {l.label}
            </button>
          ))}
          <button onClick={() => go("#prenota")} className="block font-code text-xs uppercase tracking-[0.25em] text-[#D6705A]" data-testid="nav-mobile-cta">
            {t("nav.cta")}
          </button>
        </div>
      )}
    </motion.header>
  );
};
